import { FeatureCollection, Geometry, GeoJsonProperties } from "geojson";
import { PropertyWithExamples } from "./PropertyMappingSelector.types";

// String transformation utilities
function capitalizeFirstLetter(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

function noSpaces(str: string): string {
	return str.replace(/\s+/g, "");
}

function titleCase(str: string): string {
	return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

function camelCase(str: string): string {
	return str
		.split(" ")
		.map((w, i) =>
			i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
		)
		.join("");
}

function pascalCase(str: string): string {
	return str
		.split(" ")
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join("");
}

function snakeCase(str: string): string {
	return str
		.split(" ")
		.map((w) => w.toLowerCase())
		.join("_");
}

function kebabCase(str: string): string {
	return str
		.split(" ")
		.map((w) => w.toLowerCase())
		.join("-");
}

function capitalizeFirstWord(str: string): string {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

function capitalizeLastWord(str: string): string {
	return str
		.split(" ")
		.map((w, i) =>
			i === 0 ? w.charAt(0).toLowerCase() + w.slice(1) : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase(),
		)
		.join(" ");
}

function generateTermVariants(term: string): string[] {
	// Split camelCase/PascalCase to words
	const words = term.replace(/([a-z])([A-Z])/g, "$1 $2");

	return [
		term,
		term.toLowerCase(),
		term.toUpperCase(),
		noSpaces(term), // "growername"
		kebabCase(term), // "grower-name"
		kebabCase(words), // "grower-name"
		titleCase(term), // "Grower Name"
		titleCase(words), // "Grower Name"
		pascalCase(term), // GrowerName
		pascalCase(words), // Grower Name
		camelCase(term), // growerName
		camelCase(words), // grower Name
		capitalizeFirstWord(term), // "Grower name"
		capitalizeFirstWord(words), // "Grower name"
		capitalizeLastWord(term), // "grower Name"
		capitalizeLastWord(words), // "grower Name"
		snakeCase(term), // grower_name
		snakeCase(words), // grower_name
		snakeCase(term).toUpperCase(), // GROWER_NAME
		snakeCase(words).toUpperCase(), // GROWER_NAME
	];
}

// Optimization #3: Moved generateTermVariants(property) outside the loop
export function generatePropertyVariants(property: string, baseTerms: string[]): string[] {
	const variants: string[] = [];

	// Generate property variants once, outside the loop
	const propertyVariants = generateTermVariants(property);

	for (const term of baseTerms) {
		if (!term.toLowerCase().startsWith(property)) {
			variants.push(
				property + "" + term, // proopertyterm
				property + " " + term, // property term
				property + "_" + term, // property_term
				property + "-" + term, // property-term
				(property + "" + term).toUpperCase(), // PROPERTYTERM
				(property + " " + term).toUpperCase(), // PROPERTY TERM
				(property + "_" + term).toUpperCase(), // PROPERTY_TERM
				(property + "-" + term).toUpperCase(), // PROPERTY-TERM
				capitalizeFirstLetter(property) + capitalizeFirstLetter(term), // PropertyTerm
				capitalizeFirstLetter(property) + " " + capitalizeFirstLetter(term), // Property Term
				capitalizeFirstLetter(property) + "_" + capitalizeFirstLetter(term), // Property_Term
				capitalizeFirstLetter(property) + "-" + capitalizeFirstLetter(term), // Property-Term
			);
		}

		variants.push(...generateTermVariants(term));

		// Capitalize the first letter of the first word and capitalize the remaining words
		const capitalizedTerm = term
			.split(" ")
			.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toUpperCase()))
			.join(" ");

		variants.push(...generateTermVariants(capitalizedTerm));
	}

	// Add property variants once at the end (instead of once per baseTerms iteration)
	variants.push(...propertyVariants);

	// Remove duplicates
	return Array.from(new Set(variants));
}

// Optimization #4, #6: Use for...in and simplified emptiness check
export function extractAllPropertiesFromGeoData(
	geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
): PropertyWithExamples[] {
	const propertyMap = new Map<string, Set<string>>();

	// Collect unique values for each property
	for (const geoJson of geoJsons) {
		for (const feature of geoJson.features || []) {
			const props = feature.properties;
			if (props) {
				for (const key in props) {
					const value = props[key];
					// Check for non-empty, non-whitespace values
					if (value != null && (typeof value !== "string" || value.trim() !== "")) {
						if (!propertyMap.has(key)) {
							propertyMap.set(key, new Set<string>());
						}
						// Add the string representation of the value
						propertyMap.get(key)!.add(String(value));
					}
				}
			}
		}
	}

	// Convert to array format with up to 3 examples per property
	const result: PropertyWithExamples[] = [];
	for (const [propertyName, values] of propertyMap) {
		const examples = Array.from(values).slice(0, 3);
		result.push({ name: propertyName, examples });
	}

	// Sort by property name
	return result.sort((a, b) => a.name.localeCompare(b.name));
}

// Optimization #5: Avoid array concatenation - iterate directly
export function extractPropertyFromGeoData(
	geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[],
	possibleNames: string[],
): string {
	for (const geoJson of geoJsons) {
		for (const feature of geoJson.features || []) {
			if (feature.properties) {
				for (const propertyName of possibleNames) {
					if (propertyName in feature.properties && feature.properties[propertyName]) {
						// Return the property name itself, not its value
						return propertyName;
					}
				}
			}
		}
	}

	return "";
}

// Optimization #2: Pre-compute variant lists at module level
// These are computed once when the module loads
const FIELD_VARIANTS = {
	field: generatePropertyVariants("field", ["name", "title", "field map"]),
	farm: generatePropertyVariants("farm", ["farm name"]),
	grower: generatePropertyVariants("grower", ["grower name", "client", "client name"]),
	fieldId: generatePropertyVariants("fieldId", ["id", "field key", "field guid"]),
	labName: generatePropertyVariants("lab", ["lab name", "laboratory", "testing lab"]),
	labInstructions: generatePropertyVariants("lab", ["instructions", "lab instructions", "special instructions"]),
	submissionNotes: generatePropertyVariants("submission", ["notes", "submission notes", "comments", "remarks"]),
	yieldGoal: generatePropertyVariants("yield", ["yield goal", "target yield", "expected yield"]),
	submitterName: generatePropertyVariants("submitter", ["submitter name", "submitted by", "contact name"]),
	submitterEmail: generatePropertyVariants("submitter", ["email", "submitter email", "contact email"]),
	growerPhone: generatePropertyVariants("phone", [
		"phone",
		"grower phone",
		"phone number",
		"mobile",
		"cell",
		"telephone",
	]),
	eventId: generatePropertyVariants("event", ["event id", "eventid", "event number"]),
	labSubmissionCode: generatePropertyVariants("lab", [
		"lab code",
		"submission code",
		"lab submission code",
		"reference code",
	]),
	externalId: generatePropertyVariants("external", ["external id", "externalid", "ext id", "third party id"]),
	externalFarmId: generatePropertyVariants("externalFarm", [
		"external farm id",
		"External Farm ID",
		"farm external id",
		"ext farm id",
		"externalFarmId",
	]),
	externalLabId: generatePropertyVariants("externalLab", [
		"external lab id",
		"External Lab ID",
		"lab external id",
		"ext lab id",
		"externalLabId",
	]),
	dateFieldReady: generatePropertyVariants("date", [
		"date ready",
		"field ready",
		"field ready date",
		"ready date",
		"est field ready",
		"estimated field ready",
		"DateReady",
		"ready_date",
		"fieldReady",
		"estimatedDate",
		"date_field_ready",
	]),
	density: generatePropertyVariants("density", [
		"density",
		"sample density",
		"core density",
		"sampling density",
		"cores per acre",
		"grid size",
	]),
	depth: generatePropertyVariants("depth", ["depth", "sample depth", "sampling depth", "core depth", "depth inches"]),
	frequencyOfMicros: generatePropertyVariants("frequency", [
		"frequency of micros",
		"micro frequency",
		"micronutrient frequency",
		"frequencyofmicros",
	]),
	testPackage: generatePropertyVariants("test", [
		"test package",
		"package",
		"lab package",
		"test type",
		"analysis",
		"analysis type",
		"package type",
		"test pckg",
	]),
	fieldPriority: generatePropertyVariants("priority", [
		"field priority",
		"sample priority",
		"priority level",
		"urgency",
		"priority ovrd",
		"rush",
	]),
	nextCrop: generatePropertyVariants("next", [
		"next crop",
		"following crop",
		"intended crop",
		"crop next",
		"next season",
		"planned crop",
	]),
	lastCrop: generatePropertyVariants("last", [
		"last crop",
		"previous crop",
		"prior crop",
		"crop last",
		"prev crop",
		"last season",
	]),
	branch: generatePropertyVariants("branch", [
		"branch",
		"branch name",
		"location",
		"office",
		"region",
		"division",
		"territory",
		"district",
	]),
	salesperson: generatePropertyVariants("salesperson", [
		"salesperson",
		"sales person",
		"salesperson name",
		"rep",
		"representative",
		"agent",
		"agronomist",
		"advisor",
		"sales rep",
	]),
	salespersonPhone: generatePropertyVariants("salesperson", [
		"phone",
		"mobile",
		"salesperson phone",
		"salesperson mobile",
		"sales phone",
		"rep phone",
		"agent phone",
	]),
} as const;

// Convert pre-computed variants to Sets for O(1) lookup
const FIELD_VARIANT_SETS = {
	field: new Set(FIELD_VARIANTS.field),
	farm: new Set(FIELD_VARIANTS.farm),
	grower: new Set(FIELD_VARIANTS.grower),
	fieldId: new Set(FIELD_VARIANTS.fieldId),
	labName: new Set(FIELD_VARIANTS.labName),
	labInstructions: new Set(FIELD_VARIANTS.labInstructions),
	submissionNotes: new Set(FIELD_VARIANTS.submissionNotes),
	yieldGoal: new Set(FIELD_VARIANTS.yieldGoal),
	submitterName: new Set(FIELD_VARIANTS.submitterName),
	submitterEmail: new Set(FIELD_VARIANTS.submitterEmail),
	growerPhone: new Set(FIELD_VARIANTS.growerPhone),
	eventId: new Set(FIELD_VARIANTS.eventId),
	labSubmissionCode: new Set(FIELD_VARIANTS.labSubmissionCode),
	externalId: new Set(FIELD_VARIANTS.externalId),
	externalFarmId: new Set(FIELD_VARIANTS.externalFarmId),
	externalLabId: new Set(FIELD_VARIANTS.externalLabId),
	dateFieldReady: new Set(FIELD_VARIANTS.dateFieldReady),
	density: new Set(FIELD_VARIANTS.density),
	depth: new Set(FIELD_VARIANTS.depth),
	frequencyOfMicros: new Set(FIELD_VARIANTS.frequencyOfMicros),
	testPackage: new Set(FIELD_VARIANTS.testPackage),
	fieldPriority: new Set(FIELD_VARIANTS.fieldPriority),
	nextCrop: new Set(FIELD_VARIANTS.nextCrop),
	lastCrop: new Set(FIELD_VARIANTS.lastCrop),
	branch: new Set(FIELD_VARIANTS.branch),
	salesperson: new Set(FIELD_VARIANTS.salesperson),
	salespersonPhone: new Set(FIELD_VARIANTS.salespersonPhone),
} as const;

/**
 * Auto-guess property mappings from GeoJSON data.
 * Attempts to find matching properties
 * based on common naming conventions.
 *
 * Optimized with:
 * - Pre-computed variant lists at module level
 * - Single-pass property name extraction into a Set
 * - O(1) Set-based lookups instead of O(n) feature scanning
 *
 * @param geoJsons - Array of FeatureCollections to search
 * @returns Object with guessed property names for each field, or null if no match found
 */
export function autoGuessPropertyMappings(geoJsons: FeatureCollection<Geometry, GeoJsonProperties>[]): {
	grower: string | null;
	farm: string | null;
	field: string | null;
	fieldId: string | null;
	labName: string | null;
	labInstructions: string | null;
	submissionNotes: string | null;
	yieldGoal: string | null;
	submitterName: string | null;
	submitterEmail: string | null;
	growerPhoneNumber: string | null;
	eventId: string | null;
	labSubmissionCode: string | null;
	externalId: string | null;
	externalFarmId: string | null;
	externalLabId: string | null;
	dateFieldReadyEstimated: string | null;
	density: string | null;
	depth: string | null;
	frequencyOfMicros: string | null;
	testPackage: string | null;
	fieldPriority: string | null;
	nextCrop: string | null;
	lastCrop: string | null;
	branch: string | null;
	salesperson: string | null;
	salespersonMobilePhone: string | null;
} {
	if (!geoJsons || geoJsons.length === 0) {
		return {
			grower: null,
			farm: null,
			field: null,
			fieldId: null,
			labName: null,
			labInstructions: null,
			submissionNotes: null,
			yieldGoal: null,
			submitterName: null,
			submitterEmail: null,
			growerPhoneNumber: null,
			eventId: null,
			labSubmissionCode: null,
			externalId: null,
			externalFarmId: null,
			externalLabId: null,
			dateFieldReadyEstimated: null,
			density: null,
			depth: null,
			frequencyOfMicros: null,
			testPackage: null,
			fieldPriority: null,
			nextCrop: null,
			lastCrop: null,
			branch: null,
			salesperson: null,
			salespersonMobilePhone: null,
		};
	}

	// Optimization #1: Extract all unique property names once into a Set
	// This gives us O(1) lookup instead of O(features) per field type
	const allPropertyNames = new Set<string>();
	for (const geoJson of geoJsons) {
		for (const feature of geoJson.features || []) {
			if (feature.properties) {
				for (const key in feature.properties) {
					// Only add if the property has a truthy value
					if (feature.properties[key]) {
						allPropertyNames.add(key);
					}
				}
			}
		}
	}

	// Helper function to find first matching variant using Set lookup
	function findFirstMatch(variantSet: Set<string>, variants: readonly string[]): string | null {
		for (const variant of variants) {
			if (allPropertyNames.has(variant) && variantSet.has(variant)) {
				return variant;
			}
		}
		return null;
	}

	// Use pre-computed variants with O(1) Set lookup
	return {
		field: findFirstMatch(FIELD_VARIANT_SETS.field, FIELD_VARIANTS.field),
		farm: findFirstMatch(FIELD_VARIANT_SETS.farm, FIELD_VARIANTS.farm),
		grower: findFirstMatch(FIELD_VARIANT_SETS.grower, FIELD_VARIANTS.grower),
		fieldId: findFirstMatch(FIELD_VARIANT_SETS.fieldId, FIELD_VARIANTS.fieldId),
		labName: findFirstMatch(FIELD_VARIANT_SETS.labName, FIELD_VARIANTS.labName),
		labInstructions: findFirstMatch(FIELD_VARIANT_SETS.labInstructions, FIELD_VARIANTS.labInstructions),
		submissionNotes: findFirstMatch(FIELD_VARIANT_SETS.submissionNotes, FIELD_VARIANTS.submissionNotes),
		yieldGoal: findFirstMatch(FIELD_VARIANT_SETS.yieldGoal, FIELD_VARIANTS.yieldGoal),
		submitterName: findFirstMatch(FIELD_VARIANT_SETS.submitterName, FIELD_VARIANTS.submitterName),
		submitterEmail: findFirstMatch(FIELD_VARIANT_SETS.submitterEmail, FIELD_VARIANTS.submitterEmail),
		growerPhoneNumber: findFirstMatch(FIELD_VARIANT_SETS.growerPhone, FIELD_VARIANTS.growerPhone),
		eventId: findFirstMatch(FIELD_VARIANT_SETS.eventId, FIELD_VARIANTS.eventId),
		labSubmissionCode: findFirstMatch(FIELD_VARIANT_SETS.labSubmissionCode, FIELD_VARIANTS.labSubmissionCode),
		externalId: findFirstMatch(FIELD_VARIANT_SETS.externalId, FIELD_VARIANTS.externalId),
		externalFarmId: findFirstMatch(FIELD_VARIANT_SETS.externalFarmId, FIELD_VARIANTS.externalFarmId),
		externalLabId: findFirstMatch(FIELD_VARIANT_SETS.externalLabId, FIELD_VARIANTS.externalLabId),
		dateFieldReadyEstimated: findFirstMatch(FIELD_VARIANT_SETS.dateFieldReady, FIELD_VARIANTS.dateFieldReady),
		density: findFirstMatch(FIELD_VARIANT_SETS.density, FIELD_VARIANTS.density),
		depth: findFirstMatch(FIELD_VARIANT_SETS.depth, FIELD_VARIANTS.depth),
		frequencyOfMicros: findFirstMatch(FIELD_VARIANT_SETS.frequencyOfMicros, FIELD_VARIANTS.frequencyOfMicros),
		testPackage: findFirstMatch(FIELD_VARIANT_SETS.testPackage, FIELD_VARIANTS.testPackage),
		fieldPriority: findFirstMatch(FIELD_VARIANT_SETS.fieldPriority, FIELD_VARIANTS.fieldPriority),
		nextCrop: findFirstMatch(FIELD_VARIANT_SETS.nextCrop, FIELD_VARIANTS.nextCrop),
		lastCrop: findFirstMatch(FIELD_VARIANT_SETS.lastCrop, FIELD_VARIANTS.lastCrop),
		branch: findFirstMatch(FIELD_VARIANT_SETS.branch, FIELD_VARIANTS.branch),
		salesperson: findFirstMatch(FIELD_VARIANT_SETS.salesperson, FIELD_VARIANTS.salesperson),
		salespersonMobilePhone: findFirstMatch(FIELD_VARIANT_SETS.salespersonPhone, FIELD_VARIANTS.salespersonPhone),
	};
}
